import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** A single selectable option within an option set. */
export interface OptionSetOption {
  id: string
  label: string
}

/** A reusable, named set of dropdown options. */
export interface OptionSet {
  id: string
  name: string
  description?: string | null
  options: OptionSetOption[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

/** Props accepted when creating an option set. */
export interface CreateOptionSetPayload {
  name: string
  description?: string
  options: OptionSetOption[]
}

/** Partial props for updating an option set (PUT partial merge). */
export type UpdateOptionSetPayload = Partial<CreateOptionSetPayload>

/** Query params accepted by `GET /option-sets`. */
export interface OptionSetListParams {
  q?: string
  /** Comma-separated list of option-set ids — returns exactly those sets, unpaginated. */
  ids?: string
  skip?: number
  take?: number
  /** Column sort, `<column>:<asc|desc>`, applied server-side before paging (z8nrz7e4h6). */
  sort?: string
}

/** Response of `GET /option-sets`. */
export interface OptionSetListResult {
  items: OptionSet[]
  total: number
}

/** A form that references a given option set, as returned by the usage endpoint. */
export interface OptionSetUsageForm {
  id: string
  name: string
  version: number
  category: string
}

/** Response of `GET /option-sets/:id/usage`. */
export interface OptionSetUsage {
  optionSetId: string
  count: number
  forms: OptionSetUsageForm[]
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const optionSetsApi = {
  list: (params: OptionSetListParams = {}) => {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.ids) qs.set('ids', params.ids)
    if (params.skip != null) qs.set('skip', String(params.skip))
    if (params.take != null) qs.set('take', String(params.take))
    if (params.sort) qs.set('sort', params.sort)
    const query = qs.toString()
    return request<OptionSetListResult>(`/option-sets${query ? `?${query}` : ''}`)
  },

  get: (id: string) => request<OptionSet>(`/option-sets/${id}`),

  getUsage: (id: string) => request<OptionSetUsage>(`/option-sets/${id}/usage`),

  create: (payload: CreateOptionSetPayload) =>
    request<OptionSet>('/option-sets', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: UpdateOptionSetPayload) =>
    request<OptionSet>(`/option-sets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  remove: (id: string) =>
    request<{ ok: true }>(`/option-sets/${id}`, { method: 'DELETE' }),
}
